import { countries, getCountryData, TCountryCode } from 'countries-list';
import i18nCountries from 'i18n-iso-countries';
import countriesEn from 'i18n-iso-countries/langs/en.json';
import countriesNl from 'i18n-iso-countries/langs/nl.json';
import nationalities from 'i18n-nationality';
import nationalityEn from 'i18n-nationality/langs/en.json';
import nationalityNl from 'i18n-nationality/langs/nl.json';

import { IconName } from '@/components/Icon';

import i18n from '@/i18n/i18n';

import { Tenant } from '@/types/common';
import { Option } from '@/types/components/common';
import { Language } from '@/types/i18n';
import { CountryISO } from '@/types/places';

nationalities.registerLocale(nationalityEn);
nationalities.registerLocale(nationalityNl);
i18nCountries.registerLocale(countriesEn);
i18nCountries.registerLocale(countriesNl);

const EXCLUDED_COUNTRIES = ['antarctica'];

export const POPULAR_COUNTRIES: string[] = [
  CountryISO.Cayman,
  CountryISO.US,
  CountryISO.UK,
  CountryISO.China,
  CountryISO.France,
  CountryISO.India,
];

export const POPULAR_NATIONALITIES: string[] = [
  CountryISO.Cayman,
  CountryISO.US,
  CountryISO.UK,
  CountryISO.China,
  CountryISO.France,
  CountryISO.India,
];

export const CUSTOM_COUNTRIES = [
  {
    name: 'Anguilla',
    native: 'Anguilla',
    code2: 'AI',
    code3: 'AIA',
  },
];

export const CURRENCY_SIGN: Record<string, string> = {
  BBD: 'BBD$',
};

export const LANGUAGES: Option[] = [
  {
    text: 'English',
    value: Language.English,
    icon: 'flag-united-kingdom',
  },
  {
    text: 'Nederlands',
    value: Language.Dutch,
    icon: 'flag-netherlands',
  },
];

export const getCountryLabel = (code2: string, language: Language) => {
  let label = i18nCountries.getName(code2, language) || code2;
  if (label === 'Republiek Suriname') {
    label = 'Suriname';
  }
  return label;
};

export const getStandardizeCountryName = (name: string) => {
  switch (name) {
    case 'Aland':
      return 'Aland Islands';
    case 'Cocos (Keeling) Islands':
      return 'Cocos Islands';
    case 'U.S. Virgin Islands':
      return 'Virgin Islands';
    case 'Myanmar (Burma)':
      return 'Myanmar';
    case 'Anguilla':
      return 'Anguilla, British';
    default:
      return name;
  }
};
const modifiedCountries = Object.entries(countries)
  .filter(([, item]) => !EXCLUDED_COUNTRIES.includes(item.name.toLowerCase()))
  .map(([code, value]) => ({
    ...value,
    code2: code,
    code3: getCountryData(code as TCountryCode).iso3,
  }))
  .sort((a, b) => a.name.localeCompare(b.name));

export const RAW_COUNTRIES = modifiedCountries;

export const PHONE_CODES: Option[] = modifiedCountries.map(value => {
  return {
    text: '+' + value.phone[0],
    value: '+' + value.phone[0],
  };
});
export const COUNTRIES: Option[] = modifiedCountries
  .map(value => {
    const countryFlag = value.name.toLowerCase().replace(/\s+/g, '-');
    return {
      icon: `flag-${countryFlag}` as IconName,
      text: value.name,
      value: value.code3,
    };
  })
  .sort((a, b) => (a.text || '').localeCompare(b.text || ''));

export const NATIONALITIES: Option[] = modifiedCountries
  .filter(value => !!nationalities.getName(value.code3, 'en'))
  .concat({
    name: 'Anguilla',
    native: 'Anguilla',
    phone: [1264],
    continent: 'NA',
    capital: 'The Valley',
    currency: ['XCD'],
    languages: ['en'],
    code2: 'AI',
    code3: 'AIA',
  })
  .map(value => {
    const countryText = getStandardizeCountryName(
      nationalities.getName(value.code3, 'en') || value.name,
    );
    const countryFlag = value.name.toLowerCase().replace(/\s+/g, '-');

    return {
      icon: `flag-${countryFlag}` as IconName,
      text: countryText,
      value: value.code3,
    };
  });

export const NATIONALITIES_WITH_POPULAR = COUNTRIES.map(item => {
  const isPopular = POPULAR_NATIONALITIES.includes(String(item.value));
  return {
    ...item,
    group: isPopular ? i18n.t('common.popular') : i18n.t('common.allCountries'),
    groupOrder: isPopular ? 1 : 0,
  };
}) as Option[];

export const COUNTRIES_WITH_POPULAR = COUNTRIES.map(item => {
  const isPopular = POPULAR_COUNTRIES.includes(String(item.value));
  return {
    ...item,
    group: isPopular ? i18n.t('common.popular') : i18n.t('common.allCountries'),
    groupOrder: isPopular ? 1 : 0,
  };
}) as Option[];

export const SUPPORTED_COUNTRIES = [
  Tenant.BVI,
  Tenant.Anguilla,
  Tenant.Barbados,
  Tenant.Grenada,
  Tenant.SaintKittsNevis,
  Tenant.SaintLucia,
  Tenant.SaintVincent,
  Tenant.SintMaarten,
  Tenant.Guyana,
  Tenant.Suriname,
  Tenant.Trinidad,
];

export const TRINIDAD_CITIES = ['Port of Spain', 'San Fernando'];

export const PHONE_FORM_COUNTRY_DIGIT_LIMITS = {
  '1': 10,
  '44': 11,
  '63': 10,
  '': { min: 6, max: 15 },
};

export const PHONE_REGEX = {
  tenDigits: /^\d{10}$/,
  elevenDigits: /^\d{11}$/,
  sixToFifteenDigits: /^\d{6,15}$/,
};

export const getPhoneSlotProps = (
  selectedCountryCode: string,
  overrideMaxLength?: Record<string, number>,
) => {
  let maxLength: number;
  switch (selectedCountryCode) {
    case '1-US':
    case '1-Canada':
    case '1-Jamaica':
    case '63-Philippines':
      maxLength = 10;
      break;
    case '44-UK':
      maxLength = 11;
      break;
    case 'Other':
      maxLength = overrideMaxLength?.['Other'] || 14;
      break;
    default:
      maxLength = 7;
  }
  return {
    maxLength,
  };
};

export function sanitizeCaymanCountryCode(optionValue: string): string {
  if (optionValue === 'Other') return '';
  return optionValue.split('-')[0];
}

export function sanitizeCountryCode(optionValue: string): string {
  if (optionValue === 'Other') return '';
  return optionValue;
}

export const TENANT_PARAM = 'tenant';
